import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Clear } from '@material-ui/icons';
import * as styles from './styles/styles.module.scss';
import DropzonePad from '../DropzonePad';
import Modal from '../../shared/Modal';
import TrackFileUpload from '../TrackFileUpload';
import TrackUploadFields from '../TrackUploadFields';
import TrailFormSuccess from '../../shared/TrailFormSuccess';
import useDropzoneUpload from '../../../hooks/useDropzoneUpload';
import { defaultHikingActivity } from '../../../utils/activity_util';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import * as formStyles from '../styles/styles.module.scss';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';

const TrackUploadModal = ({
  closeActiveTrailModal,
  url,
  showSmoothOption,
  trailId,
  onTrackAdd,
  exploreFlag,
  locale,
  routeMode,
  handleRouteUpload,
  createAsMap,
  systemOwned
}) => {
  const [activeFile, toggleActiveFile] = useState(null);
  const [smoothFile, toggleSmoothFile] = useState(true);
  const [progress, updateProgress] = useState(0);
  const [uploading, toggleUploading] = useState(false);
  const [map, updateMap] = useState(null);
  const [errorMessage, updateErrorMessage] = useState(null);
  const [currentStep, updateCurrentStep] = useState(0);
  const [trackFormParams, updateTrackFormParams] = useState({
    name: '',
    description: '',
    rating: '',
    activity: defaultHikingActivity(),
    date: new Date(), // this date will change once a file is uploaded
    isPrivate: false
  });

  const STEP_ZERO = 0;
  const STEP_ONE = 1;
  const STEP_TWO = 2;
  const FINAL_STEP = 3;

  const updateTrackForm = payload => {
    updateTrackFormParams(prevState => {
      return { ...prevState, ...payload };
    });
  };

  const addedFileCallback = file => {
    toggleActiveFile(file);
    updateCurrentStep(STEP_ONE);
  };

  const sendingCallback = (file, formData) => {
    formData.append('presentation_type', 'track');
    if (trailId) {
      formData.append('trail_id', trailId);
    }

    if (createAsMap) {
      formData.append('create_as_map', createAsMap);
    }

    if (smoothFile) {
      formData.append('smooth', 'on');
    }

    if (systemOwned) {
      formData.append('system_owned', true);
    }
  };

  const handleErrorCallback = () => {
    updateErrorMessage(StringConstantsMixin.strings.PROGRESS_MESSAGES.ERROR_SAVING);
    updateProgress(0);
    toggleUploading(false);
  };

  const removedFileCallback = file => {
    toggleActiveFile(null);
  };

  const progressCallback = fileProgress => {
    const progressInt = fileProgress === 100 ? 99 : parseInt(fileProgress.toFixed(0));

    updateProgress(progressInt);
  };

  const handleRouteModeUpload = uploadedMap => {
    const payload = createAsMap ? uploadedMap : uploadedMap.routes[uploadedMap.routes.length - 1];
    handleRouteUpload(payload);
    updateCurrentStep(FINAL_STEP);
  };

  const successCallback = (file, response) => {
    const uploadedMap = response.maps[0];
    updateProgress(100);
    updateMap(uploadedMap);
    toggleUploading(false);
    if (routeMode) {
      handleRouteModeUpload(uploadedMap);
      return;
    }

    updateTrackForm({ date: new Date(uploadedMap.created_at) });
    updateCurrentStep(STEP_TWO);
  };

  const updateSuccessCallback = response => {
    const { maps } = response;
    const payload = { detail: maps[0] };
    toggleUploading(false);
    updateCurrentStep(FINAL_STEP);

    if (exploreFlag) {
      const event = new CustomEvent('addTrack', payload);
      window.document.dispatchEvent(event);
      return;
    }

    onTrackAdd(payload);
  };

  const updateErrorCallback = err => {
    toggleUploading(false);
  };

  const handleDeleteSuccess = () => {
    updateMap(null);
  };

  const deleteRecording = () => {
    ServerCommunicationUtil.deleteApiEndpoint(
      `/api/alltrails/maps/${map.id}`,
      {},
      handleDeleteSuccess,
      () => {},
      () => {}
    );
  };

  const updateRecording = () => {
    toggleUploading(true);
    const updateUrl = `/explore/recording/${map.slug}`;
    const { name, description, activity, date, rating, isPrivate } = trackFormParams;
    const params = {
      rating,
      at_map: {
        name,
        description,
        activity: activity.value,
        private: isPrivate ? '1' : '0',
        created_at: date.toISOString().slice(0, 10),
        trail_id: trailId
      }
    };

    ServerCommunicationUtil.putApiEndpoint(updateUrl, params, updateSuccessCallback, updateErrorCallback, () => {}, {}, 'json');
  };

  const handleCloseModal = () => {
    if (currentStep !== FINAL_STEP && map && map.id) {
      deleteRecording();
    }

    closeActiveTrailModal();
  };

  const [dropzone, initDropzone] = useDropzoneUpload({
    url,
    formParam: '#uploadForm',
    paramName: 'file',
    maxFiles: 1,
    maxFilesize: 256,
    acceptedFiles: '.gpx,.tpo,.fit,.tcx,.kml,.kmz',
    addedFileCallback,
    handleErrorCallback,
    sendingCallback,
    removedFileCallback,
    progressCallback,
    successCallback
  });

  const openDropzone = () => {
    return dropzone.hiddenFileInput.click();
  };

  const removeFile = file => {
    dropzone.removeFile(file);
    updateCurrentStep(STEP_ZERO);

    if (map && map.id) {
      deleteRecording();
      updateProgress(0);
    }
  };

  const handleCheckboxChange = () => {
    toggleSmoothFile(prevState => !prevState);
  };

  const uploadRecording = () => {
    updateErrorMessage(null);
    toggleUploading(true);
    dropzone.processQueue();
  };

  const handleCtaClick = () => {
    if (currentStep === STEP_ONE) {
      return map && map.id ? updateCurrentStep(STEP_TWO) : uploadRecording();
    }

    return updateRecording();
  };

  const handleCancelClick = () => {
    if (currentStep === STEP_ZERO || currentStep === FINAL_STEP) {
      return;
    }

    if (currentStep === STEP_ONE) {
      removeFile(activeFile);
      updateCurrentStep(STEP_ZERO);
    }

    if (currentStep === STEP_TWO) {
      updateCurrentStep(STEP_ONE);
    }
  };

  const getCtaText = () => {
    if (currentStep === STEP_ZERO || currentStep === STEP_ONE) {
      return StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.NEXT;
    }

    return StringConstantsMixin.strings.UPLOAD;
  };

  const renderActiveStep = () => {
    if (map && map.id && currentStep === STEP_TWO) {
      return <TrackUploadFields {...trackFormParams} updateTrackForm={updateTrackForm} locale={locale} />;
    }

    if (activeFile && currentStep === STEP_ONE) {
      return (
        <>
          <TrackFileUpload
            file={activeFile}
            removeFile={removeFile}
            showSmoothOption={showSmoothOption}
            smoothFile={smoothFile}
            handleCheckboxChange={handleCheckboxChange}
            progress={progress}
            uploaded={map && map.id}
            uploading={uploading}
          />
          {errorMessage && <div className={formStyles.serverError}>{errorMessage}</div>}
        </>
      );
    }

    return <DropzonePad variant={routeMode ? 'route' : 'tracks'} openDropzone={openDropzone} />;
  };

  const activeStep = renderActiveStep();

  return (
    <Modal handleBackgroundClick={handleCloseModal}>
      <div className={formStyles.formUploadContainer}>
        <div className={formStyles.clearContainer}>
          <button type="button" className={formStyles.clearIcon} onClick={handleCloseModal} data-testid="closeIcon">
            <Clear />
          </button>
        </div>
        {currentStep === FINAL_STEP ? (
          <TrailFormSuccess variant={routeMode ? 'route' : 'track'} />
        ) : (
          <>
            <div className={styles.header}>
              <div className={formStyles.title} data-testid="drawRoute">
                {routeMode ? StringConstantsMixin.strings.CONTRIBUTE.UPLOAD_ROUTE : StringConstantsMixin.strings.CONTRIBUTE.UPLOAD_RECORDING}
              </div>
              {currentStep !== STEP_ZERO && (
                <div className={styles.stepText}>{StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.stepText(currentStep, 2)}</div>
              )}
            </div>
            <div className={formStyles.form} id="uploadForm">
              <div className={styles.dropContainer}>{activeStep}</div>
              <div className={formStyles.buttonContainer}>
                <button
                  type="button"
                  onClick={handleCtaClick}
                  className={formStyles.uploadButton}
                  data-testid="nextButton"
                  disabled={activeFile === null || uploading || currentStep === STEP_ZERO}
                >
                  {getCtaText()}
                </button>
                {currentStep !== STEP_ZERO && (
                  <button className={formStyles.cancelButton} type="button" onClick={handleCancelClick}>
                    {StringConstantsMixin.strings.CANCEL}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

TrackUploadModal.propTypes = {
  closeActiveTrailModal: PropTypes.func.isRequired,
  url: PropTypes.string,
  showSmoothOption: PropTypes.bool,
  trailId: PropTypes.number,
  onTrackAdd: PropTypes.func,
  exploreFlag: PropTypes.bool,
  routeMode: PropTypes.bool,
  handleRouteUpload: PropTypes.func,
  createAsMap: PropTypes.bool,
  systemOwned: PropTypes.bool
};

TrackUploadModal.defaultProps = {
  url: '/api/alltrails/maps/gpx',
  showSmoothOption: true,
  trailId: null,
  onTrackAdd: () => {},
  exploreFlag: false,
  routeMode: false,
  handleRouteUpload: () => {},
  createAsMap: false,
  systemOwned: false
};

export default TrackUploadModal;
